import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useUIStore } from './ui'
import apiService from '../services/apiService'
import logService from '../services/logService'

export const useSettingsStore = defineStore('settings', () => {
  const uiStore = useUIStore()

  // State
  const settings = ref({})
  const allSettings = ref([])
  const pagination = ref({
    current_page: 1,
    per_page: 50,
    total: 0,
    last_page: 1
  })
  const filters = ref({
    search: '',
    group: '',
    type: ''
  })
  const loading = ref(false)

  // Fetch public settings (for frontend)
  const fetchSettings = async () => {
    try {
      loading.value = true
      logService.info('Fetching settings...')

      const response = await apiService.get('/settings')

      if (response.success) {
        settings.value = response.data
        return response.data
      } else {
        throw new Error(response.message || 'Failed to fetch settings')
      }
    } catch (error) {
      logService.error('Fetch Settings Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return {}
    } finally {
      loading.value = false
    }
  }

  // Fetch all settings for admin (with pagination and filters)
  const fetchAllSettings = async (page = 1, customFilters = null) => {
    try {
      loading.value = true

      const params = {
        page,
        per_page: pagination.value.per_page,
        ...(customFilters || filters.value)
      }

      logService.info('Fetching all settings...', params)

      const response = await apiService.get('/admin/settings', { params })

      if (response.success) {
        allSettings.value = response.data
        pagination.value = response.pagination
        return response.data
      } else {
        throw new Error(response.message || 'Failed to fetch settings')
      }
    } catch (error) {
      logService.error('Fetch All Settings Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return []
    } finally {
      loading.value = false
    }
  }

  // Fetch single setting by ID
  const fetchSetting = async (id) => {
    try {
      loading.value = true
      logService.info('Fetching setting:', id)

      const response = await apiService.get(`/admin/settings/${id}`)

      if (response.success) {
        return response.data
      } else {
        throw new Error(response.message || 'Failed to fetch setting')
      }
    } catch (error) {
      logService.error('Fetch Setting Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  // Create new setting
  const createSetting = async (settingData) => {
    try {
      loading.value = true
      logService.info('Creating setting...', settingData)

      const response = await apiService.post('/admin/settings', settingData)

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Setting Created',
          message: 'Setting has been created successfully.'
        })

        // Refresh the settings list
        await fetchAllSettings(pagination.value.current_page)

        return response.data
      } else {
        throw new Error(response.message || 'Failed to create setting')
      }
    } catch (error) {
      logService.error('Create Setting Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Create Failed',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  // Update existing setting
  const updateSetting = async (id, settingData) => {
    try {
      loading.value = true
      logService.info('Updating setting:', id, settingData)

      const response = await apiService.put(`/admin/settings/${id}`, settingData)

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Setting Updated',
          message: 'Setting has been updated successfully.'
        })

        // Refresh the settings list
        await fetchAllSettings(pagination.value.current_page)

        return response.data
      } else {
        throw new Error(response.message || 'Failed to update setting')
      }
    } catch (error) {
      logService.error('Update Setting Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Update Failed',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  // Delete setting
  const deleteSetting = async (id) => {
    try {
      loading.value = true
      logService.info('Deleting setting:', id)

      const response = await apiService.delete(`/admin/settings/${id}`)

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Setting Deleted',
          message: 'Setting has been deleted successfully.'
        })

        // Refresh the settings list
        await fetchAllSettings(pagination.value.current_page)

        return true
      } else {
        throw new Error(response.message || 'Failed to delete setting')
      }
    } catch (error) {
      logService.error('Delete Setting Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Delete Failed',
        message: error.message
      })
      return false
    } finally {
      loading.value = false
    }
  }

  // Bulk update settings
  const bulkUpdateSettings = async (settingsArray) => {
    try {
      loading.value = true
      logService.info('Bulk updating settings...', settingsArray)

      const response = await apiService.post('/admin/settings/bulk-update', {
        settings: settingsArray
      })

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Settings Updated',
          message: 'Settings have been updated successfully.'
        })

        // Refresh public settings and all settings
        await fetchSettings()
        await fetchAllSettings(pagination.value.current_page)

        return true
      } else {
        throw new Error(response.message || 'Failed to update settings')
      }
    } catch (error) {
      logService.error('Bulk Update Settings Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Update Failed',
        message: error.message
      })
      return false
    } finally {
      loading.value = false
    }
  }

  // Legacy: Update settings (for backward compatibility)
  const updateSettings = async (settingsData) => {
    try {
      loading.value = true
      logService.info('Updating settings...', settingsData)

      const response = await apiService.put('/settings', settingsData)

      if (response.success) {
        settings.value = response.data

        uiStore.showNotification({
          type: 'success',
          title: 'Settings Updated',
          message: 'Settings have been updated successfully.'
        })

        return response.data
      } else {
        throw new Error(response.message || 'Failed to update settings')
      }
    } catch (error) {
      logService.error('Update Settings Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Update Failed',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  // Getters
  const getContactInfo = () => {
    return {
      email: settings.value.contact_email || '',
      phone: settings.value.contact_phone || '',
      whatsapp: settings.value.whatsapp_number || '',
      address: settings.value.address || '',
      office_hours: settings.value.office_hours || ''
    }
  }

  const getSocialLinks = () => {
    return {
      facebook: settings.value.facebook_url || '',
      twitter: settings.value.twitter_url || '',
      linkedin: settings.value.linkedin_url || '',
      instagram: settings.value.instagram_url || '',
      github: settings.value.github_url || '',
      youtube: settings.value.youtube_url || ''
    }
  }

  const getCompanyInfo = () => {
    return {
      name: settings.value.company_name || '',
      description: settings.value.company_description || '',
      site_title: settings.value.site_title || ''
    }
  }

  // Get unique groups from all settings
  const getGroups = () => {
    const groups = [...new Set(allSettings.value.map(s => s.group))]
    return groups.sort()
  }

  // Get unique types from all settings
  const getTypes = () => {
    const types = [...new Set(allSettings.value.map(s => s.type))]
    return types.sort()
  }

  return {
    // State
    settings,
    allSettings,
    pagination,
    filters,
    loading,

    // Actions
    fetchSettings,
    fetchAllSettings,
    fetchSetting,
    createSetting,
    updateSetting,
    deleteSetting,
    bulkUpdateSettings,
    updateSettings, // Legacy

    // Getters
    getContactInfo,
    getSocialLinks,
    getCompanyInfo,
    getGroups,
    getTypes
  }
})
